import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
    Button,
    Card,
    Checkbox,
    Collapse,
    Input,
    InputNumber,
    Radio,
    Space,
    Tabs,
    Tag,
    Typography,
    List,
    Table,
    Popover,
    Modal,
    Divider
} from "antd";
import { SettingOutlined, SearchOutlined } from "@ant-design/icons";
import { t } from "i18next";
import { useHistory } from "react-router-dom";
import SimpleLayout from "../../core/components/layout/SimpleLayout";
import Helper from "../../core/Helper";
import ApiService from "../../core/service/ApiService";
import { useUser } from "../../core/context/UserContext";
import "./PriceUpdate.css";

const { Panel } = Collapse;
const { TabPane } = Tabs;

// Adjustment modes
const ADJUSTMENT_MODE = {
    PERCENTAGE: 'percentage',     // Yüzde artış/azalış
    FIXED_AMOUNT: 'fixedAmount',  // Sabit tutar artış/azalış
    FIXED_PRICE: 'fixedPrice'     // Sabit fiyat (direkt atama)
};

// Performance optimized input component for table
const PriceOverrideInput = ({ value, onChange, ...props }) => {
    const [localValue, setLocalValue] = useState(value);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    const handleBlur = () => {
        if (localValue !== value) {
            onChange(localValue);
        }
    };

    const handlePressEnter = (e) => {
        e.target.blur();
    };

    return (
        <InputNumber
            {...props}
            value={localValue}
            onChange={setLocalValue}
            onBlur={handleBlur}
            onPressEnter={handlePressEnter}
        />
    );
};

export default function PriceUpdate() {
    const history = useHistory();
    const { user } = useUser();

    // Data loading states
    const [operations, setOperations] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const [appointmentTypes, setAppointmentTypes] = useState([]);
    const [units, setUnits] = useState([]);

    // Tab 1: Base price update
    const [selectedOperations, setSelectedOperations] = useState([]);
    const [selectedOperationTags, setSelectedOperationTags] = useState([]);
    const [baseAdjustment, setBaseAdjustment] = useState({
        mode: ADJUSTMENT_MODE.PERCENTAGE,
        isIncrease: true,
        value: 0
    });
    const [basePriceChanges, setBasePriceChanges] = useState([]);

    // Tab 2: Institution/Type selection (merged)
    const [selectedInstitutions, setSelectedInstitutions] = useState([]);
    const [selectedAppointmentTypes, setSelectedAppointmentTypes] = useState([]);
    const [excludedUnits, setExcludedUnits] = useState([]); // Global excluded units
    const [institutionSettings, setInstitutionSettings] = useState({}); // { insId: { excludedUnits: [], selectedTags: [], priceOverrides: {} } }

    // UI State
    const [activeDetailTarget, setActiveDetailTarget] = useState(null); // id of the active institution/type in preview
    const [targetSelectionVisible, setTargetSelectionVisible] = useState(false);

    const [priceMode, setPriceMode] = useState('current'); // 'current' or 'base'
    const [currentAdjustment, setCurrentAdjustment] = useState({
        mode: ADJUSTMENT_MODE.PERCENTAGE,
        isIncrease: true,
        value: 0
    });

    // Tag filter for Tab 2
    const [selectedTags, setSelectedTags] = useState([]);

    // Preview for Tab 2
    const [institutionPrices, setInstitutionPrices] = useState([]);

    // Search filters
    const [operationSearch, setOperationSearch] = useState("");
    const [institutionSearch, setInstitutionSearch] = useState("");
    const [previewSearchText, setPreviewSearchText] = useState(""); // Search for preview table

    // Active tab
    const [activeTab, setActiveTab] = useState("1");


    // Get unique tags from all operations
    const uniqueTags = useMemo(() => {
        const _tags = []
        operations.forEach(op => {
            if (op.tagList) {
                op.tagList.forEach(tagId => {
                    if (!_tags.includes(tagId)) {
                        _tags.push(tagId);
                    }
                });
            }
        });
        return _tags.sort((a, b) => a?.localeCompare(b));
    }, [operations]);

    // Load initial data
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        const [opsRes, insRes, typesRes, unitsRes] = await Promise.all([
            ApiService.Operation.GetAllOperationList(),
            ApiService.Institution.GetInstitutionList(true),
            ApiService.Appointment.GetAppointmentTypes(),
            ApiService.Unit.GetUnitList()
        ]);

        if (opsRes.success) {
            setOperations(opsRes.data?.filter(a => a.unit?.enabled)?.sort((a, b) => {
                const unitCompare = a.unit?.name?.localeCompare(b.unit?.name);
                if (unitCompare !== 0) return unitCompare;
                return a.name?.localeCompare(b.name);
            }));
        }

        if (insRes.success) setInstitutions(insRes.data);
        if (typesRes.success) setAppointmentTypes(typesRes.data);
        if (unitsRes.success) setUnits(unitsRes.data);
    };

    // Filter operations based on search (includes tag search)
    const filteredOperations = useMemo(() => {
        let result = operations;

        // Tag filter
        if (selectedOperationTags.length > 0) {
            result = result.filter(op => {
                const tagList = op.tagList || op.TagList || [];
                return tagList.some(t => selectedOperationTags.includes(t));
            });
        }

        if (!operationSearch) return result;
        const searchNormalized = Helper.Text.normalizeText(operationSearch);

        return result.filter(op => {
            const tagList = op.tagList?.join(',');

            return Helper.Text.normalizeText(op.name).includes(searchNormalized) ||
                Helper.Text.normalizeText(tagList).includes(searchNormalized);
        });
    }, [operations, operationSearch, selectedOperationTags, user]);

    // Filter institutions based on search
    const filteredInstitutions = useMemo(() => {
        if (!institutionSearch) return institutions;
        return institutions.filter(ins =>
            Helper.Text.normalizeText(ins.name).includes(Helper.Text.normalizeText(institutionSearch))
        );
    }, [institutions, institutionSearch]);

    // Calculate new price
    const calculateNewPrice = useCallback((currentPrice, adjustment) => {
        if (!adjustment || adjustment.value === 0) return currentPrice;

        switch (adjustment.mode) {
            case ADJUSTMENT_MODE.PERCENTAGE:
                const multiplier = adjustment.isIncrease ? (1 + adjustment.value / 100) : (1 - adjustment.value / 100);
                return Math.round(currentPrice * multiplier * 100) / 100;
            case ADJUSTMENT_MODE.FIXED_AMOUNT:
                const newPrice = adjustment.isIncrease ? currentPrice + adjustment.value : currentPrice - adjustment.value;
                return Math.max(0, newPrice);
            case ADJUSTMENT_MODE.FIXED_PRICE:
                return adjustment.value;
            default:
                return currentPrice;
        }
    }, []);

    // Calculate base price changes preview (only for selected operations)
    useEffect(() => {
        const changes = selectedOperations.map(opId => {
            const op = operations.find(o => o.id === opId);
            if (!op) return null;
            const newPrice = calculateNewPrice(op.price, baseAdjustment);
            return {
                operationId: opId,
                name: op.name,
                unitName: op.unit?.name,
                currentPrice: op.price,
                newPrice: newPrice,
                diff: newPrice - op.price
            };
        }).filter(Boolean);
        setBasePriceChanges(changes);
    }, [selectedOperations, baseAdjustment, operations, calculateNewPrice]);

    // Load institution prices for preview
    const loadInstitutionPrices = async (institutionId) => {
        // Get all operation IDs
        const operationIds = operations.map(o => o.id);
        const res = await ApiService.Operation.GetInstitutionPriceList(institutionId, operationIds);
        if (res.success) {
            setInstitutionPrices(res.data || []);
        }
    };

    // Load appointment type prices for preview
    const loadAppointmentTypePrices = async (typeId) => {
        // Get all operation IDs
        const operationIds = operations.map(o => o.id);
        const res = await ApiService.Operation.GetAppointmentTypePriceList(typeId, operationIds);
        if (res.success) {
            setInstitutionPrices(res.data || []);
        }
    };

    // Load preview when active target changes
    useEffect(() => {
        if (activeDetailTarget && activeTab === "2" && operations.length > 0) {
            // Check if it's an institution
            if (institutions.some(i => i.id === activeDetailTarget)) {
                loadInstitutionPrices(activeDetailTarget);
            } else if (appointmentTypes.some(t => t.id === activeDetailTarget)) {
                loadAppointmentTypePrices(activeDetailTarget);
            }
        } else {
            setInstitutionPrices([]);
        }
    }, [activeDetailTarget, activeTab, operations, institutions, appointmentTypes]);

    // Generate preview for tab 2
    const getInstitutionPreview = useCallback(() => {
        if (!activeDetailTarget || institutionPrices.length === 0) return [];

        // Check target type
        const isInstitution = institutions.some(i => i.id === activeDetailTarget);
        const isAppointmentType = appointmentTypes.some(t => t.id === activeDetailTarget);

        if (!isInstitution && !isAppointmentType) return [];

        let prices = institutionPrices;

        const effectiveExcluded = getEffectiveExcludedUnits(activeDetailTarget);
        const effectiveTags = getEffectiveSelectedTags(activeDetailTarget);

        // Filter by excluded units
        if (effectiveExcluded.length > 0) {
            prices = prices.filter(p => {
                const op = operations.find(o => o.id === p.operationId);
                if (effectiveExcluded.includes(op.unitId))
                    return false;

                return true;
            });
        }
        console.log(effectiveExcluded, effectiveTags, prices)

        // Filter by tags
        if (effectiveTags.length > 0) {
            prices = prices.filter(p => {
                const op = operations.find(o => o.id === p.operationId);
                const tagList = op?.tagList || op?.TagList || [];
                return tagList.some(t => effectiveTags.includes(t));
            });
        }

        const mappedPrices = prices.map(price => {
            const operation = operations.find(o => o.id === price.operationId);

            const basePrice = priceMode === 'current' ? price.price : (operation?.price || price.price);
            let newPrice = calculateNewPrice(basePrice, currentAdjustment);

            // Check override
            if (institutionSettings[activeDetailTarget]?.priceOverrides?.[price.operationId] !== undefined) {
                newPrice = institutionSettings[activeDetailTarget].priceOverrides[price.operationId];
            }

            return {
                operationId: operation?.id,
                operationName: operation?.name,
                unitName: operation?.unit?.name,
                tagList: operation?.tagList || operation?.TagList || [],
                currentPrice: price.price,
                baseOperationPrice: operation?.price,
                operationPrice: price.operationPrice || price.price,
                newPrice: newPrice,
                diff: newPrice - price.price
            };
        });

        // Sort by Unit Name then Operation Name
        return mappedPrices.sort((a, b) => {
            const unitCompare = a.unitName?.localeCompare(b.unitName);
            if (unitCompare !== 0) return unitCompare;
            return a.operationName?.localeCompare(b.operationName);
        });
    }, [activeDetailTarget, institutionPrices, institutionSettings, operations, priceMode, currentAdjustment, calculateNewPrice, previewSearchText]);

    // Helpers for granular settings
    const getEffectiveExcludedUnits = (insId) => {
        if (institutionSettings[insId] && institutionSettings[insId].excludedUnits) {
            return institutionSettings[insId].excludedUnits;
        }
        return excludedUnits;
    };

    const getEffectiveSelectedTags = (insId) => {
        if (institutionSettings[insId] && institutionSettings[insId].selectedTags) {
            return institutionSettings[insId].selectedTags;
        }
        return selectedTags;
    };

    const updateInstitutionSetting = (insId, field, value) => {
        setInstitutionSettings(prev => ({
            ...prev,
            [insId]: {
                ...prev[insId],
                [field]: value
            }
        }));
    };

    // Override price for specific operation in specific institution
    const handlePriceOverride = (insId, opId, value) => {
        // Ensure the institution/type is selected for saving
        if (institutions.some(i => i.id === insId)) {
            if (!selectedInstitutions.includes(insId)) {
                setSelectedInstitutions(prev => [...prev, insId]);
            }
        } else if (appointmentTypes.some(t => t.id === insId)) {
            if (!selectedAppointmentTypes.includes(insId)) {
                setSelectedAppointmentTypes(prev => [...prev, insId]);
            }
        }

        setInstitutionSettings(prev => ({
            ...prev,
            [insId]: {
                ...prev[insId],
                priceOverrides: {
                    ...(prev[insId]?.priceOverrides || {}),
                    [opId]: value
                }
            }
        }));
    };

    const handleTagSelect = (tag, checked) => {
        if (checked) {
            setSelectedTags(prev => [...prev, tag]);
        } else {
            setSelectedTags(prev => prev.filter(t => t !== tag));
        }
    };

    // Handle select all operations
    const handleSelectAllOperations = (checked) => {
        if (checked) {
            setSelectedOperations(filteredOperations.map(op => op.id));
        } else {
            setSelectedOperations([]);
        }
    };

    // Handle operation selection
    const handleOperationSelect = (opId, checked) => {
        if (checked) {
            setSelectedOperations([...selectedOperations, opId]);
        } else {
            setSelectedOperations(selectedOperations.filter(id => id !== opId));
        }
    };

    // Handle institution selection
    const handleInstitutionSelect = (insId, checked) => {
        if (checked) {
            setSelectedInstitutions([...selectedInstitutions, insId]);
        } else {
            setSelectedInstitutions(selectedInstitutions.filter(id => id !== insId));
        }
    };

    // Handle select all institutions
    const handleSelectAllInstitutions = (checked) => {
        if (checked) {
            setSelectedInstitutions(filteredInstitutions.map(i => i.id));
        } else {
            setSelectedInstitutions([]);
        }
    };

    const handleSelectAllAppointmentTypes = (checked) => {
        if (checked) {
            setSelectedAppointmentTypes(appointmentTypes.map(t => t.id));
        } else {
            setSelectedAppointmentTypes([]);
        }
    };

    // Global Unit Management Handlers
    const handleGlobalUnitToggle = (unitId, checked) => {
        // If checked, remove from excluded (include it)
        // If unchecked, add to excluded (exclude it)
        if (checked) {
            setExcludedUnits(excludedUnits.filter(id => id !== unitId));
        } else {
            setExcludedUnits([...excludedUnits, unitId]);
        }
    };

    const handleSelectAllUnits = () => {
        setExcludedUnits([]);
    };

    const handleDeselectAllUnits = () => {
        const allUnitIds = units.map(u => u.id);
        setExcludedUnits(allUnitIds);
    };

    // Handle appointment type selection
    const handleAppointmentTypeSelect = (typeId, checked) => {
        if (checked) {
            setSelectedAppointmentTypes([...selectedAppointmentTypes, typeId]);
        } else {
            setSelectedAppointmentTypes(selectedAppointmentTypes.filter(id => id !== typeId));
        }
    };

    // Handle unit exclusion
    const handleUnitExclude = (unitId, checked) => {
        if (checked) {
            setExcludedUnits(excludedUnits.filter(id => id !== unitId));
        } else {
            setExcludedUnits([...excludedUnits, unitId]);
        }
    };



    // Handle operation tag selection (Tab 1)
    const handleOperationTagSelect = (tagId, checked) => {
        if (checked) {
            setSelectedOperationTags([...selectedOperationTags, tagId]);
        } else {
            setSelectedOperationTags(selectedOperationTags.filter(id => id !== tagId));
        }
    };

    // Handle unit selection for Tab 1 (Cari Fiyat)
    const handleTab1UnitSelect = (unitId, checked) => {
        // Get all operations belonging to this unit
        const unitOperationIds = filteredOperations
            .filter(op => op.unitId === unitId)
            .map(op => op.id);

        if (checked) {
            // Add all unit operations to selected (avoid duplicates)
            setSelectedOperations(prev => {
                const newSet = new Set([...prev, ...unitOperationIds]);
                return Array.from(newSet);
            });
        } else {
            // Remove all unit operations from selected
            setSelectedOperations(prev => prev.filter(id => !unitOperationIds.includes(id)));
        }
    };

    // Check if all operations of a unit are selected
    const isUnitFullySelected = (unitId) => {
        const unitOperationIds = filteredOperations
            .filter(op => op.unitId === unitId)
            .map(op => op.id);
        return unitOperationIds.length > 0 && unitOperationIds.every(id => selectedOperations.includes(id));
    };

    // Check if some (but not all) operations of a unit are selected
    const isUnitPartiallySelected = (unitId) => {
        const unitOperationIds = filteredOperations
            .filter(op => op.unitId === unitId)
            .map(op => op.id);
        const selectedCount = unitOperationIds.filter(id => selectedOperations.includes(id)).length;
        return selectedCount > 0 && selectedCount < unitOperationIds.length;
    };

    // Get unique units from filtered operations
    const filteredUnits = useMemo(() => {
        const unitIds = new Set();
        const result = [];
        filteredOperations.forEach(op => {
            if (op.unitId && !unitIds.has(op.unitId)) {
                unitIds.add(op.unitId);
                result.push({
                    id: op.unitId,
                    name: op.unit?.name,
                    firmName: op.unit?.firm?.name
                });
            }
        });
        return result.sort((a, b) => a.name?.localeCompare(b.name));
    }, [filteredOperations]);

    // Get institution-specific units
    const getInstitutionUnits = (institutionId) => {
        return units;
    };

    // Convert adjustment to API format
    const toApiAdjustment = (adj) => ({
        mode: adj.mode === ADJUSTMENT_MODE.PERCENTAGE ? 0 :
            adj.mode === ADJUSTMENT_MODE.FIXED_AMOUNT ? 1 : 2,
        isIncrease: adj.isIncrease,
        value: adj.value
    });

    // Save handlers
    const handleSaveBasePrices = async () => {
        if (basePriceChanges.length === 0) return;

        const updates = basePriceChanges.map(change => ({
            operationId: change.operationId,
            newPrice: change.newPrice
        }));

        const res = await ApiService.BulkPriceUpdate.UpdateBaseOperationPrices(updates);
        if (res.success) {
            loadData();
            setSelectedOperations([]);
            setBaseAdjustment({ mode: ADJUSTMENT_MODE.PERCENTAGE, isIncrease: true, value: 0 });
        }
    };

    // Save handler with scope support
    // scope: 'all' | 'active'
    // Save handler
    const handleSaveInstitutionPrices = async () => {
        if (selectedInstitutions.length === 0 && selectedAppointmentTypes.length === 0) return;

        // Group institutions by their effective configuration (excludedUnits & tags)
        const groups = {};

        selectedInstitutions.forEach(insId => {
            const effectiveExcluded = getEffectiveExcludedUnits(insId).sort().join(',');
            const effectiveTags = getEffectiveSelectedTags(insId).sort().join(',');
            const key = `${effectiveExcluded}|${effectiveTags}`;

            if (!groups[key]) {
                groups[key] = {
                    institutionIds: [],
                    excludedUnitIds: getEffectiveExcludedUnits(insId),
                    tagIds: getEffectiveSelectedTags(insId)
                };
            }
            groups[key].institutionIds.push(insId);
        });

        // Create requests for institution groups
        const promises = Object.values(groups).map(group => {
            const request = {
                targets: {
                    institutionIds: group.institutionIds,
                    appointmentTypeIds: [], // Specific requests for institutions
                    excludedUnitIds: group.excludedUnitIds,
                    tagIds: group.tagIds
                },
                defaultAdjustment: toApiAdjustment(currentAdjustment),
                institutionAdjustments: [],
                appointmentTypeAdjustments: []
            };

            // Add price overrides for institutions in this group
            group.institutionIds.forEach(insId => {
                if (institutionSettings[insId]?.priceOverrides) {
                    Object.entries(institutionSettings[insId].priceOverrides).forEach(([opId, newPrice]) => {
                        request.institutionAdjustments.push({
                            institutionId: insId,
                            operationId: parseInt(opId, 10),
                            adjustment: {
                                mode: 2, // FixedPrice
                                isIncrease: true,
                                value: newPrice
                            }
                        });
                    });
                }
            });

            return priceMode === 'current'
                ? ApiService.BulkPriceUpdate.UpdatePricesFromCurrent(request)
                : ApiService.BulkPriceUpdate.UpdatePricesFromBase(request);
        });

        // Handle Appointment Types separately if needed (assuming simple batch for them as they don't have unit exclusions in this UI context yet)
        if (selectedAppointmentTypes.length > 0) {
            const request = {
                targets: {
                    institutionIds: [],
                    appointmentTypeIds: selectedAppointmentTypes,
                    excludedUnitIds: [],
                    tagIds: []
                },
                defaultAdjustment: toApiAdjustment(currentAdjustment),
                institutionAdjustments: [],
                appointmentTypeAdjustments: []
            };

            // Add price overrides for appointment types
            selectedAppointmentTypes.forEach(typeId => {
                if (institutionSettings[typeId]?.priceOverrides) {
                    Object.entries(institutionSettings[typeId].priceOverrides).forEach(([opId, newPrice]) => {
                        request.appointmentTypeAdjustments.push({
                            appointmentTypeId: typeId,
                            operationId: parseInt(opId, 10),
                            adjustment: {
                                mode: 2, // FixedPrice
                                isIncrease: true,
                                value: newPrice
                            }
                        });
                    });
                }
            });

            promises.push(
                priceMode === 'current'
                    ? ApiService.BulkPriceUpdate.UpdatePricesFromCurrent(request)
                    : ApiService.BulkPriceUpdate.UpdatePricesFromBase(request)
            );
        }

        const results = await Promise.all(promises);
        const allSuccess = results.every(res => res.success);

        if (allSuccess) {
            if (Helper.Toast) Helper.Toast.Show('Başarıyla kaydedildi', 'success'); // Assuming Helper.Toast exists, fallback if not
            loadData();
            setSelectedInstitutions([]);
            setSelectedAppointmentTypes([]);
            setActiveDetailTarget(null);
            setInstitutionSettings({});
        }
    };

    // Render adjustment controls
    const renderAdjustmentControls = (adjustment, setAdjustment, label = t('Güncelleme'), actionButton = null) => (
        <div className="adjustment-row">
            <label>{label}:</label>
            <Radio.Group
                value={adjustment.mode}
                onChange={(e) => setAdjustment({ ...adjustment, mode: e.target.value })}
            >
                <Radio value={ADJUSTMENT_MODE.PERCENTAGE}>{t('Yüzde')}</Radio>
                <Radio value={ADJUSTMENT_MODE.FIXED_AMOUNT}>{t('Sabit Tutar')}</Radio>
                <Radio value={ADJUSTMENT_MODE.FIXED_PRICE}>{t('Sabit Fiyat')}</Radio>
            </Radio.Group>
            {adjustment.mode !== ADJUSTMENT_MODE.FIXED_PRICE && (
                <Radio.Group
                    value={adjustment.isIncrease}
                    onChange={(e) => setAdjustment({ ...adjustment, isIncrease: e.target.value })}
                >
                    <Radio value={true}>{t('Artış')}</Radio>
                    <Radio value={false}>{t('Azalış')}</Radio>
                </Radio.Group>
            )}
            <InputNumber
                min={0}
                value={adjustment.value}
                onChange={(val) => setAdjustment({ ...adjustment, value: val || 0 })}
                addonAfter={adjustment.mode === ADJUSTMENT_MODE.PERCENTAGE ? '%' : '₺'}
                style={{ width: 120 }}
            />
            {actionButton && (
                <div style={{ marginLeft: 'auto' }}>
                    {actionButton}
                </div>
            )}
        </div>
    );

    // Render operation item for virtual list
    const renderOperationItem = (op) => {
        const tagList = op.tagList || op.TagList || [];
        return (
            <div key={op.id} className="selection-item">
                <Checkbox
                    checked={selectedOperations.includes(op.id)}
                    onChange={(e) => handleOperationSelect(op.id, e.target.checked)}
                >
                    <span className="selection-item-name">{op.unit?.name} - {op.name}</span>
                </Checkbox>
                <div className="selection-item-tags">
                    {tagList.slice(0, 3).map(tagId => (
                        <Tag key={tagId} style={{ fontSize: 10, padding: '0 4px' }}>
                            {tagId}
                        </Tag>
                    ))}
                    {tagList.length > 3 && <span style={{ fontSize: 10, color: '#999' }}>+{tagList.length - 3}</span>}
                </div>
                <span className="selection-item-price">
                    {Helper.Currency.format(op.price)}
                </span>
            </div>
        );
    };

    const renderSelectableTags = () => {
        return (
            <>
                <div className="section-title">{t('Etiketler')}</div>
                <div style={{ marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {
                        uniqueTags.map(tag => (
                            <Tag.CheckableTag
                                key={tag}
                                color="magenta"
                                checked={selectedOperationTags.includes(tag)}
                                onChange={(checked) => handleOperationTagSelect(tag, checked)}
                                style={{ border: '1px solid #d9d9d9', minWidth: 50 }}
                            >
                                {tag}
                            </Tag.CheckableTag>
                        ))}
                </div>
            </>
        )
    }

    // Render institution/type selection with units
    const renderTargetSelection = () => (
        <div className="selection-list">
            <div className="section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{t('Kurum Seçimi')}</span>
                <Popover
                    trigger="click"
                    placement="bottomRight"
                    content={
                        <div style={{ width: 300, maxHeight: 400, overflow: 'auto' }}>
                            <div style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 8 }}>
                                <Button size="small" type="link" onClick={handleSelectAllUnits}>
                                    {t('Tümünü Seç')}
                                </Button>
                                <Button size="small" type="link" danger onClick={handleDeselectAllUnits}>
                                    {t('Hiçbirini Seçme')}
                                </Button>
                            </div>
                            {units.map(unit => (
                                <div key={unit.id} style={{ padding: '4px 0' }}>
                                    <Checkbox
                                        checked={!excludedUnits.includes(unit.id)}
                                        onChange={(e) => handleGlobalUnitToggle(unit.id, e.target.checked)}
                                    >
                                        {unit.firm?.name} - {unit.name}
                                    </Checkbox>
                                </div>
                            ))}
                        </div>
                    }
                >
                    <Button icon={<SettingOutlined />} size="small">
                        {t('Birimleri Yönet')}
                    </Button>
                </Popover>
            </div>
            <Input
                placeholder={t('Kurum Ara...')}
                value={institutionSearch}
                onChange={(e) => setInstitutionSearch(e.target.value)}
                className="selection-search"
                allowClear
            />
            <div style={{ marginBottom: 8 }}>
                <Checkbox
                    indeterminate={selectedInstitutions.length > 0 && selectedInstitutions.length < filteredInstitutions.length}
                    checked={selectedInstitutions.length === filteredInstitutions.length && filteredInstitutions.length > 0}
                    onChange={(e) => handleSelectAllInstitutions(e.target.checked)}
                >
                    {t('Tümünü Seç')}
                </Checkbox>
            </div>
            <List
                className="selection-virtual-list"
                dataSource={filteredInstitutions}
                height={200}
                itemHeight={40}
                itemKey="id"
                renderItem={ins => (
                    <div key={ins.id} className="selection-item">
                        <Checkbox
                            checked={selectedInstitutions.includes(ins.id)}
                            onChange={(e) => handleInstitutionSelect(ins.id, e.target.checked)}
                        >
                            {ins.name}
                        </Checkbox>
                    </div>
                )}
            />

            <div className="section-title" style={{ marginTop: 16 }}>{t('Randevu Tipi Seçimi')}</div>
            <div style={{ marginBottom: 8 }}>
                <Checkbox
                    indeterminate={selectedAppointmentTypes.length > 0 && selectedAppointmentTypes.length < appointmentTypes.length}
                    checked={selectedAppointmentTypes.length === appointmentTypes.length && appointmentTypes.length > 0}
                    onChange={(e) => handleSelectAllAppointmentTypes(e.target.checked)}
                >
                    {t('Tümünü Seç')}
                </Checkbox>
            </div>
            {appointmentTypes.map(type => (
                <div key={type.id} className="selection-item">
                    <Checkbox
                        checked={selectedAppointmentTypes.includes(type.id)}
                        onChange={(e) => handleAppointmentTypeSelect(type.id, e.target.checked)}
                    >
                        {type.name}
                    </Checkbox>
                </div>
            ))}
            <Divider orientation="left" style={{ margin: '24px 0 12px 0' }}>{t('Etiket Filtresi')}</Divider>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {uniqueTags.map(tag => (
                    <Tag.CheckableTag
                        key={tag}
                        color="magenta"
                        checked={selectedTags.includes(tag)}
                        onChange={(checked) => handleTagSelect(tag, checked)}
                        style={{ border: '1px solid #d9d9d9', minWidth: 50 }}
                    >
                        {tag}
                    </Tag.CheckableTag>
                ))}
            </div>
        </div>
    );

    return (
        <SimpleLayout fullWidth={true}>
            <Card>
                <div className="price-update-header">
                    <Space>
                        <Button size="small" onClick={() => history.push('/app/system/operations')}>
                            {t('Geri')}
                        </Button>
                        <Typography.Title level={4} style={{ margin: 0 }}>
                            {t('Fiyat Güncelleme')}
                        </Typography.Title>
                    </Space>
                </div>

                <Tabs activeKey={activeTab} onChange={setActiveTab}>
                    {/* Tab 1: Base Price Update */}
                    <TabPane tab={t('Cari Fiyat')} key="1">
                        <div className="tab-content">
                            {renderAdjustmentControls(baseAdjustment, setBaseAdjustment, t('Güncelleme'), (
                                <Button type="primary" onClick={handleSaveBasePrices}>
                                    {t('Kaydet')}
                                </Button>
                            ))}

                            <div className="price-update-content">
                                <div className="price-update-selection">
                                    {renderSelectableTags()}

                                    {/* Birim Seçimi */}
                                    <div className="section-title">{t('Birim Seçimi')}</div>
                                    <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 4, maxHeight: 150, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 4, padding: 8 }}>
                                        {filteredUnits.map(unit => (
                                            <Tag.CheckableTag
                                                key={unit.id}
                                                checked={isUnitFullySelected(unit.id)}
                                                onChange={(checked) => handleTab1UnitSelect(unit.id, checked)}
                                                style={{
                                                    border: '1px solid #d9d9d9',
                                                    minWidth: 80,
                                                    backgroundColor: isUnitFullySelected(unit.id) ? '#1890ff' : isUnitPartiallySelected(unit.id) ? '#e6f7ff' : '#fff',
                                                    color: isUnitFullySelected(unit.id) ? '#fff' : '#333'
                                                }}
                                            >
                                                {unit.name}
                                            </Tag.CheckableTag>
                                        ))}
                                    </div>

                                    <div className="section-title">{t('İşlem Seçimi')}</div>
                                    <Input
                                        placeholder={t('İşlem veya etiket ara...')}
                                        value={operationSearch}
                                        onChange={(e) => setOperationSearch(e.target.value)}
                                        className="selection-search"
                                        allowClear
                                    />
                                    <div style={{ marginBottom: 8 }}>
                                        <Checkbox
                                            indeterminate={selectedOperations.length > 0 && selectedOperations.length < filteredOperations.length}
                                            checked={selectedOperations.length === filteredOperations.length && filteredOperations.length > 0}
                                            onChange={(e) => handleSelectAllOperations(e.target.checked)}
                                        >
                                            {t('Tümünü Seç')} ({filteredOperations.length})
                                        </Checkbox>
                                    </div>
                                    {/* Virtual List for performance with large datasets */}
                                    <List
                                        className="selection-virtual-list"
                                        dataSource={filteredOperations}
                                        height={400}
                                        itemHeight={40}
                                        itemKey="id"
                                        renderItem={renderOperationItem}
                                    />
                                </div>

                                <div className="price-update-preview">
                                    <div className="section-title">
                                        {t('Önizleme')} ({basePriceChanges.length} {t('işlem')})
                                    </div>
                                    {basePriceChanges.length === 0 ? (
                                        <div className="empty-selection">{t('İşlem seçiniz')}</div>
                                    ) : (
                                        <>
                                            <table className="preview-table">
                                                <thead>
                                                    <tr>
                                                        <th>{t('Birim')}</th>
                                                        <th>{t('İşlem')}</th>
                                                        <th>{t('Mevcut Fiyat')}</th>
                                                        <th>{t('Yeni Fiyat')}</th>
                                                        <th>{t('Fark')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {basePriceChanges.slice(0, 50).map(change => (
                                                        <tr key={change.operationId}>
                                                            <td>{change.unitName}</td>
                                                            <td>{change.name}</td>
                                                            <td>{Helper.Currency.format(change.currentPrice)}</td>
                                                            <td>{Helper.Currency.format(change.newPrice)}</td>
                                                            <td className={change.diff >= 0 ? 'price-increase' : 'price-decrease'}>
                                                                {change.diff >= 0 ? '+' : ''}{Helper.Currency.format(change.diff)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            {basePriceChanges.length > 50 && (
                                                <Typography.Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
                                                    ... {t('ve')} {basePriceChanges.length - 50} {t('işlem daha')}
                                                </Typography.Text>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </TabPane>

                    {/* Tab 2: Institution/Type Price Update */}
                    <TabPane tab={t('Kurum/Tip Fiyatları')} key="2">
                        <div className="tab-content">
                            {/* Price Mode Selection */}
                            <div className="adjustment-row">
                                <label>{t('Kaynak Fiyat')}:</label>
                                <Radio.Group
                                    value={priceMode}
                                    onChange={(e) => setPriceMode(e.target.value)}
                                >
                                    <Radio value="current">{t('Mevcut Fiyat Üzerinden')}</Radio>
                                    <Radio value="base">{t('Cari Fiyat Üzerinden')}</Radio>
                                </Radio.Group>
                            </div>

                            {renderAdjustmentControls(currentAdjustment, setCurrentAdjustment, t('Güncelleme'), (
                                <Button type="primary" onClick={() => handleSaveInstitutionPrices()}>
                                    {t('Kaydet')}
                                </Button>
                            ))}

                            <div className="price-update-content" style={{ display: 'flex', gap: 16 }}>
                                {/* Left Side: Selected List */}
                                <div style={{ width: 250, display: 'flex', flexDirection: 'column' }}>
                                    <div className="section-title">
                                        {t('Seçili Hedefler')}
                                        <Popover
                                            content={<div style={{ width: 400, padding: 8 }}>{renderTargetSelection()}</div>}
                                            trigger="click"
                                            placement="bottomLeft"
                                            visible={targetSelectionVisible}
                                            onVisibleChange={setTargetSelectionVisible}
                                        >
                                            <Button size="small" type="primary" ghost style={{ float: 'right' }}>
                                                {t('Hedef Seçimi')}
                                            </Button>
                                        </Popover>
                                    </div>
                                    <div style={{ flex: 1, border: '1px solid #f0f0f0', borderRadius: 4, overflow: 'auto' }}>
                                        {selectedInstitutions.length === 0 && selectedAppointmentTypes.length === 0 && (
                                            <div style={{ padding: 16, color: '#999', textAlign: 'center' }}>
                                                {t('Lütfen "Hedef Seçimi" butonunu kullanarak kurum veya randevu tipi ekleyiniz.')}
                                            </div>
                                        )}
                                        {selectedInstitutions.length > 0 && (
                                            <>
                                                <div style={{ padding: '8px 12px', background: '#fafafa', fontWeight: 600 }}>{t('Kurumlar')}</div>
                                                {selectedInstitutions.map(id => {
                                                    const ins = institutions.find(i => i.id === id);
                                                    return (
                                                        <div
                                                            key={id}
                                                            className={`selection-item ${activeDetailTarget === id ? 'active-selection' : ''}`}
                                                            style={{ background: activeDetailTarget === id ? '#e6f7ff' : 'transparent', cursor: 'pointer' }}
                                                            onClick={() => setActiveDetailTarget(id)}
                                                        >
                                                            <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {ins?.name}
                                                            </div>
                                                            {institutionSettings[id] && <Tag color="blue">*</Tag>}
                                                        </div>
                                                    );
                                                })}
                                            </>
                                        )}
                                        {selectedAppointmentTypes.length > 0 && (
                                            <>
                                                <div style={{ padding: '8px 12px', background: '#fafafa', fontWeight: 600 }}>{t('Randevu Tipleri')}</div>
                                                {selectedAppointmentTypes.map(id => {
                                                    const type = appointmentTypes.find(t => t.id === id);
                                                    return (
                                                        <div
                                                            key={id}
                                                            className={`selection-item ${activeDetailTarget === id ? 'active-selection' : ''}`}
                                                            style={{ background: activeDetailTarget === id ? '#e6f7ff' : 'transparent', cursor: 'pointer' }}
                                                            onClick={() => setActiveDetailTarget(id)}
                                                        >
                                                            <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {type?.name}
                                                            </div>
                                                            {institutionSettings[id] && <Tag color="blue">*</Tag>}
                                                        </div>
                                                    );
                                                })}
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Right Side: Detail/Preview */}
                                <div className="price-update-preview" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    {!activeDetailTarget ? (
                                        <div className="empty-selection">{t('Detaylarını görmek için soldan bir hedef seçiniz')}</div>
                                    ) : (
                                        <>
                                            {/* Preview Header / Toolbar */}
                                            {(institutions.some(i => i.id === activeDetailTarget) || appointmentTypes.some(i => i.id === activeDetailTarget)) && (
                                                <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div className="section-title" style={{ margin: 0 }}>
                                                        {institutions.find(i => i.id === activeDetailTarget)?.name || appointmentTypes.find(t => t.id === activeDetailTarget)?.name} ({t('Seçili İşlemler')}: {getInstitutionPreview()?.length})
                                                    </div>
                                                    <Space>
                                                        <Popover
                                                            title={t('Birim Yönetimi')}
                                                            trigger="click"
                                                            placement="bottomRight"
                                                            content={
                                                                <div style={{ width: 300, maxHeight: 400, overflow: 'auto' }}>
                                                                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                                                        <Button size="small" type="link" onClick={() => updateInstitutionSetting(activeDetailTarget, 'excludedUnits', [])}>{t('Tümünü Seç')}</Button>
                                                                        <Button size="small" type="link" danger onClick={() => updateInstitutionSetting(activeDetailTarget, 'excludedUnits', units.map(u => u.id))}>{t('Hiçbirini Seçme')}</Button>
                                                                    </div>
                                                                    {units.map(unit => (
                                                                        <div key={unit.id} style={{ padding: '2px 0' }}>
                                                                            <Checkbox
                                                                                checked={!getEffectiveExcludedUnits(activeDetailTarget).includes(unit.id)}
                                                                                onChange={(e) => {
                                                                                    const current = getEffectiveExcludedUnits(activeDetailTarget);
                                                                                    const newExcluded = e.target.checked
                                                                                        ? current.filter(id => id !== unit.id)
                                                                                        : [...current, unit.id];
                                                                                    updateInstitutionSetting(activeDetailTarget, 'excludedUnits', newExcluded);
                                                                                }}
                                                                            >
                                                                                {unit.firm?.name} - {unit.name}
                                                                            </Checkbox>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            }
                                                        >
                                                            <Button size="small" icon={<SettingOutlined />}>{t('Birimler')}</Button>
                                                        </Popover>
                                                        <Popover
                                                            title={t('Etiket Yönetimi')}
                                                            trigger="click"
                                                            placement="bottomRight"
                                                            content={
                                                                <div style={{ width: 300, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                                    {uniqueTags.map(tag => (
                                                                        <Tag.CheckableTag
                                                                            key={tag}
                                                                            checked={getEffectiveSelectedTags(activeDetailTarget).includes(tag)}
                                                                            onChange={(checked) => {
                                                                                const current = getEffectiveSelectedTags(activeDetailTarget);
                                                                                const newTags = checked ? [...current, tag] : current.filter(t => t !== tag);
                                                                                updateInstitutionSetting(activeDetailTarget, 'selectedTags', newTags);
                                                                            }}
                                                                            style={{ border: '1px solid #d9d9d9' }}
                                                                        >
                                                                            {tag}
                                                                        </Tag.CheckableTag>
                                                                    ))}
                                                                </div>
                                                            }
                                                        >
                                                            <Button size="small" icon={<SettingOutlined />}>{t('Etiketler')}</Button>
                                                        </Popover>
                                                    </Space>
                                                </div>
                                            )}

                                            {/* Preview List */}
                                            <div style={{ marginBottom: 8 }}>
                                                <Input
                                                    placeholder={t('İşlem adı veya etiket ile ara...')}
                                                    value={previewSearchText}
                                                    onChange={(e) => setPreviewSearchText(e.target.value)}
                                                    prefix={<SearchOutlined style={{ color: '#ccc' }} />}
                                                    allowClear
                                                />
                                            </div>
                                            <Table
                                                size="small"
                                                dataSource={getInstitutionPreview().filter(item => {
                                                    if (!previewSearchText) return true;
                                                    const search = Helper.Text.normalizeText(previewSearchText);
                                                    const nameMatch = Helper.Text.normalizeText(item.operationName).includes(search);
                                                    const tagMatch = item.tagList && item.tagList.some(t => Helper.Text.normalizeText(t) == search);
                                                    return nameMatch || tagMatch;
                                                })}
                                                rowKey="operationId"
                                                pagination={false}
                                                scroll={{ y: 'calc(100vh - 490px)' }}
                                                columns={[
                                                    {
                                                        title: t('Birim'),
                                                        dataIndex: 'unitName',
                                                        key: 'unitName',
                                                        width: 150,
                                                        ellipsis: true,
                                                    },
                                                    {
                                                        title: t('İşlem'),
                                                        dataIndex: 'operationName',
                                                        key: 'operationName',
                                                        ellipsis: true,
                                                    },
                                                    {
                                                        title: t('Etiketler'),
                                                        dataIndex: 'tagList',
                                                        key: 'tagList',
                                                        width: 150,
                                                        render: (tags) => (
                                                            <div style={{ display: 'flex', gap: 2, flexWrap: 'nowrap', overflow: 'hidden' }}>
                                                                {tags && tags.slice(0, 2).map(tag => (
                                                                    <Tag key={tag} style={{ fontSize: 10, padding: '0 4px', margin: 0 }}>
                                                                        {tag}
                                                                    </Tag>
                                                                ))}
                                                                {tags && tags.length > 2 && <span style={{ fontSize: 10, color: '#999' }}>+{tags.length - 2}</span>}
                                                            </div>
                                                        )
                                                    },
                                                    {
                                                        title: t('Cari Fiyat'),
                                                        dataIndex: 'baseOperationPrice',
                                                        key: 'baseOperationPrice',
                                                        width: 90,
                                                        align: 'right',
                                                        render: (val) => Helper.Currency.format(val)
                                                    },
                                                    {
                                                        title: t('Mevcut'),
                                                        dataIndex: 'currentPrice',
                                                        key: 'currentPrice',
                                                        width: 90,
                                                        align: 'right',
                                                        render: (val) => Helper.Currency.format(val)
                                                    },
                                                    {
                                                        title: t('Yeni'),
                                                        dataIndex: 'newPrice',
                                                        key: 'newPrice',
                                                        width: 120,
                                                        render: (val, record) => (
                                                            <PriceOverrideInput
                                                                value={val}
                                                                onChange={(v) => handlePriceOverride(activeDetailTarget, record.operationId, v)}
                                                                style={{ width: '100%' }}
                                                                size="small"
                                                                formatter={value => `₺ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                                parser={value => value.replace(/\₺\s?|(,*)/g, '')}
                                                            />
                                                        )
                                                    },
                                                    {
                                                        title: t('Fark'),
                                                        dataIndex: 'diff',
                                                        key: 'diff',
                                                        width: 100,
                                                        align: 'right',
                                                        render: (val) => (
                                                            <span style={{ color: val >= 0 ? '#52c41a' : '#ff4d4f' }}>
                                                                {val > 0 ? '+' : ''}{Helper.Currency.format(val)}
                                                            </span>
                                                        )
                                                    }
                                                ]}
                                            />

                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </TabPane>
                </Tabs>
            </Card>
        </SimpleLayout>
    );
}
